import { getToken, refreshToken, waitForToken } from './auth';

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

if (!API_BASE) {
  console.warn('[api] VITE_API_BASE_URL is not set');
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Per-request retry sentinel. Set to `true` on the second attempt that
// follows a 401 + refreshToken() so we never infinite-loop on a token the
// backend keeps rejecting.
type ApiRequestInit = RequestInit & { __retriedAfter401?: boolean };

export async function apiFetch<T = unknown>(
  path: string,
  init: ApiRequestInit = {},
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const method = init.method || 'GET';
  console.info(`[api] ${method} ${path}`);

  // Read the cached token. Never initialize App Bridge here — that's App.tsx's
  // job. If the cache is empty, wait up to 5s for the App-level retry loop
  // to fill it. After that, throw a generic "token not ready" — this string
  // intentionally does NOT mention host or VITE_SHOPIFY_API_KEY because those
  // diagnostics are App-Bridge-init-time concerns and don't belong on the
  // request path.
  let token = getToken();
  if (!token) {
    token = await waitForToken(5000);
  }
  const tokenPresent = Boolean(token);
  console.info(`[api] token present: ${tokenPresent}`);
  if (!token) {
    const devToken = import.meta.env.VITE_DEV_ADMIN_TOKEN;
    if (devToken) {
      token = devToken;
      console.warn('[api] using VITE_DEV_ADMIN_TOKEN fallback');
    } else {
      throw new Error('Shopify token not ready yet, please retry');
    }
  }
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !headers.has('Content-Type') && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  let resp: Response;
  try {
    resp = await fetch(url, { ...init, headers });
  } catch (err) {
    console.error('[api] fetch failed (network/CORS):', url, err);
    throw err;
  }
  const text = await resp.text();
  let body: unknown = text;
  try { body = text ? JSON.parse(text) : null; } catch { /* keep as text */ }
  console.info('[api] ←', resp.status, url, typeof body === 'object' && body ? `keys=${Object.keys(body as object).join(',')}` : `len=${text.length}`);

  // Shopify session JWTs expire ~60s after issue. App.tsx fetches one at
  // mount, but apiFetch read it from cache without ever asking for a fresh
  // one — so any request fired more than a minute after page load came in
  // with an expired token and got 401, including tanstack-query polling on
  // the imports/tire detail pages. Refresh once on 401 and retry; if the
  // second attempt also 401s, surface the original error like before.
  if (resp.status === 401 && !init.__retriedAfter401) {
    console.warn('[api] 401 → refreshing Shopify session token + one retry');
    const fresh = await refreshToken();
    if (fresh) {
      return apiFetch<T>(path, { ...init, __retriedAfter401: true });
    }
    console.warn('[api] 401 retry skipped — refreshToken() returned null');
  }

  if (!resp.ok) {
    // Surface the most human-readable message the server sent. Order of
    // preference: `message` (Postgres error text, app-level detail) →
    // `error` (type tag like 'unauthorized', 'validation_error') → HTTP
    // status. The previous code read only `body.error`, which caused
    // generic 500s from pg to render as the literal string "error" (pg's
    // err.name is lowercase 'error') — losing the actual diagnostic.
    let msg: string;
    if (body && typeof body === 'object') {
      const b = body as any;
      const fromMessage = typeof b.message === 'string' && b.message.length > 0 ? b.message : null;
      const fromError = b.error ? String(b.error) : null;
      const base = fromMessage ?? fromError ?? `HTTP ${resp.status}`;
      if (Array.isArray(b.issues) && b.issues.length) {
        const detail = b.issues.map((i: any) => `${i.path || '?'}: ${i.message}`).join('; ');
        msg = `${base} — ${detail}`;
      } else {
        msg = base;
      }
      console.error('[api] error response body:', body);
    } else {
      msg = `HTTP ${resp.status}`;
      console.error('[api] error response (non-JSON):', text.slice(0, 300));
    }
    throw new ApiError(msg, resp.status, body);
  }
  return body as T;
}

export function qs(params: Record<string, unknown>): string {
  // Array values are emitted as repeated query params (?brand=foo&brand=bar)
  // so the backend's z.preprocess(arrayParam) sees a string[]. Empty arrays
  // and undefined / null / '' are dropped entirely so the URL stays short.
  const pairs: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      for (const item of v) {
        if (item === undefined || item === null || item === '') continue;
        pairs.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(item))}`);
      }
      continue;
    }
    pairs.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return pairs.length ? `?${pairs.join('&')}` : '';
}
